import { useState, useMemo, useEffect } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { useExecutions } from '../../context/ExecutionContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { formatLotsQty } from '../../utils/lotSize'
import { exportToExcel } from '../../utils/exportExcel'
import { API_BASE, getAuthFetchHeaders } from '../../utils/api'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import type { Trade } from '../../types'

type Tab = 'strategy' | 'trader' | 'scrip' | 'pnl' | 'transactions'
type PnlDimension = 'overall' | 'strategy' | 'scrip' | 'trader' | 'set'

interface DailyPnlRow {
  date: string
  dateLabel: string
  realized: number
  unrealized: number
  total: number
  entries: number
  exits: number
  slHits: number
  targets: number
  rsiPnl: number
  trendPnl: number
}

/**
 * Build real daily P&L rows from actual trade records.
 * Groups trades by calendar date, sums realized P&L per day.
 */
function buildRealPnl(trades: Trade[], executions: any[], days: number): DailyPnlRow[] {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - days)
  const cutoffStr = cutoff.toISOString().split('T')[0]

  // Collect all dates with trades in range
  const dateMap: Record<string, DailyPnlRow> = {}

  for (const t of trades) {
    const date = t.timestamp?.split('T')[0]
    if (!date || date < cutoffStr) continue
    if (!dateMap[date]) {
      const d = new Date(date + 'T00:00:00')
      dateMap[date] = {
        date,
        dateLabel: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        realized: 0, unrealized: 0, total: 0,
        entries: 0, exits: 0, slHits: 0, targets: 0,
        rsiPnl: 0, trendPnl: 0,
      }
    }
    const row = dateMap[date]
    const exec = executions.find(e => e.id === t.executionId)
    const isRsi = exec?.strategyType === 'rsi_straddle'
    const isTrend = exec?.strategyType === 'trending_expiry'
    const pnl = t.pnl || 0

    if (t.tradeType === 'entry' || t.tradeType === 'reentry') {
      row.entries++
    }
    if (t.tradeType?.startsWith('exit') || t.tradeType === 'panic_exit') {
      row.exits++
      row.realized += pnl
      row.total += pnl
      if (isRsi) row.rsiPnl += pnl
      if (isTrend) row.trendPnl += pnl
    }
    if (t.tradeType === 'exit_sl') row.slHits++
    if (t.tradeType === 'exit_target') row.targets++
  }

  // Add unrealized from running executions for today
  const todayStr = new Date().toISOString().split('T')[0]
  if (!dateMap[todayStr]) {
    const d = new Date()
    dateMap[todayStr] = {
      date: todayStr,
      dateLabel: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      realized: 0, unrealized: 0, total: 0,
      entries: 0, exits: 0, slHits: 0, targets: 0,
      rsiPnl: 0, trendPnl: 0,
    }
  }
  for (const exec of executions) {
    if (exec.status === 'running') {
      const mtm = exec.state?.mtm
      if (mtm) {
        dateMap[todayStr].unrealized += mtm.unrealizedPnl || 0
        dateMap[todayStr].total += mtm.unrealizedPnl || 0
      }
    }
  }

  return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date))
}

const strategyMeta: Record<string, { label: string; icon: string; color: string }> = {
  rsi_straddle: { label: 'RSI Straddle', icon: '⟐', color: '#38bdf8' },
  trending_expiry: { label: 'Trending Expiry', icon: '⏱', color: '#a78bfa' },
}

// ── Fetch real trader names from /api/users ──
function useTraderNames(): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({})
  useEffect(() => {
    fetch(`${API_BASE}/api/users`, { headers: getAuthFetchHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!Array.isArray(data)) return
        const map: Record<string, string> = {}
        for (const u of data) {
          if (u.id) map[u.id] = u.name || u.username || u.id
        }
        setNames(map)
      })
      .catch(() => {})
  }, [])
  return names
}


// ─── Multi-leg grouping logic ──────────────────────────────
interface StraddleRound {
  roundNum: number
  timestamp: string
  type: 'entry' | 'reentry' | 'adjustment'
  ceLeg: Trade | null
  peLeg: Trade | null
  ceExit: Trade | null
  peExit: Trade | null
  extraLegs: Trade[]
  extraExits: Trade[]
  totalPnl: number
  status: 'open' | 'partial' | 'closed'
}

function groupTradesIntoRounds(setTrades: Trade[]): StraddleRound[] {
  const sorted = [...setTrades].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  const rounds: StraddleRound[] = []
  const usedIds = new Set<string>()

  // Find entry/reentry pairs first
  const entries = sorted.filter(t => t.tradeType === 'entry' || t.tradeType === 'reentry')
  const adjustments = sorted.filter(t => t.tradeType === 'adjustment')
  const exits = sorted.filter(t => t.tradeType.startsWith('exit') || t.tradeType === 'panic_exit')

  // Group entries by timestamp proximity (within 5 seconds = same straddle entry)
  const entryGroups: Trade[][] = []
  let currentGroup: Trade[] = []
  for (const entry of entries) {
    if (currentGroup.length === 0) {
      currentGroup.push(entry)
    } else {
      const timeDiff = Math.abs(new Date(entry.timestamp).getTime() - new Date(currentGroup[0].timestamp).getTime())
      if (timeDiff < 5000 && entry.tradeType === currentGroup[0].tradeType) {
        currentGroup.push(entry)
      } else {
        entryGroups.push(currentGroup)
        currentGroup = [entry]
      }
    }
  }
  if (currentGroup.length > 0) entryGroups.push(currentGroup)

  let roundNum = 1
  for (const group of entryGroups) {
    const ceLeg = group.find(t => t.optionType === 'CE') || null
    const peLeg = group.find(t => t.optionType === 'PE') || null
    if (ceLeg) usedIds.add(ceLeg.id)
    if (peLeg) usedIds.add(peLeg.id)

    // Find matching exits for each leg
    let ceExit: Trade | null = null
    let peExit: Trade | null = null
    if (ceLeg) {
      ceExit = exits.find(t => !usedIds.has(t.id) && t.optionType === 'CE' && t.strike === ceLeg.strike && t.entryPrice === ceLeg.entryPrice) || null
      if (ceExit) usedIds.add(ceExit.id)
    }
    if (peLeg) {
      peExit = exits.find(t => !usedIds.has(t.id) && t.optionType === 'PE' && t.strike === peLeg.strike && t.entryPrice === peLeg.entryPrice) || null
      if (peExit) usedIds.add(peExit.id)
    }

    // Find adjustments that happened after this round
    const roundTime = new Date(group[0].timestamp).getTime()
    const nextGroupTime = entryGroups.indexOf(group) < entryGroups.length - 1
      ? new Date(entryGroups[entryGroups.indexOf(group) + 1][0].timestamp).getTime()
      : Infinity
    const roundAdjustments = adjustments.filter(t => {
      const adjTime = new Date(t.timestamp).getTime()
      return !usedIds.has(t.id) && adjTime >= roundTime && adjTime < nextGroupTime
    })
    roundAdjustments.forEach(t => usedIds.add(t.id))

    // Find exits for adjustments
    const extraExits: Trade[] = []
    for (const adj of roundAdjustments) {
      const adjExit = exits.find(t => !usedIds.has(t.id) && t.optionType === adj.optionType && t.strike === adj.strike && t.entryPrice === adj.entryPrice) || null
      if (adjExit) {
        usedIds.add(adjExit.id)
        extraExits.push(adjExit)
      }
    }

    const allPnls = [ceExit?.pnl || 0, peExit?.pnl || 0, ...extraExits.map(e => e.pnl || 0)]
    const totalPnl = allPnls.reduce((s, p) => s + p, 0)

    const ceOpen = ceLeg && !ceExit
    const peOpen = peLeg && !peExit
    const status = (ceOpen || peOpen) ? (ceExit || peExit ? 'partial' : 'open') : 'closed'

    rounds.push({
      roundNum: roundNum++,
      timestamp: group[0].timestamp,
      type: group[0].tradeType as 'entry' | 'reentry',
      ceLeg, peLeg, ceExit, peExit,
      extraLegs: roundAdjustments,
      extraExits,
      totalPnl,
      status,
    })
  }

  return rounds
}

const mono = { fontFamily: "'JetBrains Mono', monospace" }
const pnlStyle = (pnl?: number) => ({
  ...mono,
  fontWeight: 600,
  color: pnl !== undefined ? (pnl >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--text-muted)',
})

const legStatusBadge = (entry: Trade | null, exit: Trade | null) => {
  if (!entry) return null
  if (!exit) return <span className="badge badge-target" style={{ fontSize: '0.625rem' }}>OPEN</span>
  if (exit.tradeType === 'exit_sl') return <span className="badge badge-sl" style={{ fontSize: '0.625rem' }}>SL HIT</span>
  if (exit.tradeType === 'exit_target') return <span className="badge badge-target" style={{ fontSize: '0.625rem' }}>TARGET</span>
  if (exit.tradeType === 'panic_exit') return <span className="badge badge-exit" style={{ fontSize: '0.625rem' }}>PANIC</span>
  return <span className="badge badge-entry" style={{ fontSize: '0.625rem' }}>{exit.tradeType.replace(/_/g, ' ')}</span>
}

// ─── Expandable Set Row Component ──────────────────────────
function SetLegTable({ setTrades, setName, stratColor, execInfo }: { setTrades: Trade[]; setName: string; stratColor: string; execInfo?: { underlying?: string; expiry?: string; strike?: string; lots?: number; posType?: string } }) {
  const [expanded, setExpanded] = useState(true)
  const rounds = useMemo(() => groupTradesIntoRounds(setTrades), [setTrades])
  const totalPnl = rounds.reduce((s, r) => s + r.totalPnl, 0)

  if (rounds.length === 0) return null

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.5rem 0.75rem', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
          background: 'var(--accent-glow)', borderLeft: `3px solid ${stratColor}`,
          transition: 'background 0.2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>▶</span>
          <strong style={{ fontSize: '0.8125rem' }}>{setName}</strong>
          {execInfo && (
            <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}>
              <span className="badge badge-target" style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem' }}>{execInfo.underlying}</span>
              <span className="badge badge-entry" style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem' }}>{execInfo.expiry}</span>
              <span className="badge badge-exit" style={{ fontSize: '0.6875rem', padding: '0.125rem 0.5rem' }}>{execInfo.strike}{execInfo.posType === 'strangle' ? ' ⚖ Strangle' : ''}</span>
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{execInfo.lots ? formatLotsQty(execInfo.lots, execInfo.underlying || 'NIFTY') : ''}</span>
            </span>
          )}
          <span style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>{rounds.length} round{rounds.length > 1 ? 's' : ''}</span>
        </div>
        <span style={{ ...mono, fontSize: '0.8125rem', fontWeight: 700, color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
          {totalPnl >= 0 ? '+' : ''}₹{totalPnl.toLocaleString()}
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: '0.25rem' }}>
          <div className="table-wrap">
            <table style={{ fontSize: '0.75rem' }}>
              <thead>
                <tr>
                  <th style={{ width: '30px' }}>Rd</th>
                  <th>Time</th>
                  <th>Type</th>
                  <th colSpan={5} style={{ textAlign: 'center', background: 'rgba(34,197,94,0.06)', borderBottom: '2px solid var(--green)' }}>
                    📞 CALL (CE) Leg
                  </th>
                  <th colSpan={5} style={{ textAlign: 'center', background: 'rgba(239,68,68,0.06)', borderBottom: '2px solid var(--red)' }}>
                    📉 PUT (PE) Leg
                  </th>
                  <th>Total P&L</th>
                </tr>
                <tr>
                  <th></th><th></th><th></th>
                  {/* CE sub-headers */}
                  <th style={{ color: 'var(--green)', fontSize: '0.625rem' }}>Strike</th>
                  <th style={{ color: 'var(--green)', fontSize: '0.625rem' }}>Entry</th>
                  <th style={{ color: 'var(--green)', fontSize: '0.625rem' }}>Exit</th>
                  <th style={{ color: 'var(--green)', fontSize: '0.625rem' }}>P&L</th>
                  <th style={{ color: 'var(--green)', fontSize: '0.625rem' }}>Status</th>
                  {/* PE sub-headers */}
                  <th style={{ color: 'var(--red)', fontSize: '0.625rem' }}>Strike</th>
                  <th style={{ color: 'var(--red)', fontSize: '0.625rem' }}>Entry</th>
                  <th style={{ color: 'var(--red)', fontSize: '0.625rem' }}>Exit</th>
                  <th style={{ color: 'var(--red)', fontSize: '0.625rem' }}>P&L</th>
                  <th style={{ color: 'var(--red)', fontSize: '0.625rem' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((r) => (
                  <>
                    <tr key={`r-${r.roundNum}`} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{r.roundNum}</td>
                      <td style={{ fontSize: '0.625rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(r.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <span className={`badge ${r.type === 'entry' ? 'badge-target' : r.type === 'reentry' ? 'badge-entry' : 'badge-exit'}`}
                          style={{ fontSize: '0.5625rem' }}>{r.type}</span>
                      </td>
                      {/* CE leg */}
                      {r.ceLeg ? (<>
                        <td style={mono}>{r.ceLeg.strike}</td>
                        <td style={mono}>₹{r.ceLeg.entryPrice}</td>
                        <td style={mono}>{r.ceExit ? `₹${r.ceExit.exitPrice}` : '—'}</td>
                        <td style={pnlStyle(r.ceExit?.pnl)}>{r.ceExit?.pnl !== undefined ? `${r.ceExit.pnl >= 0 ? '+' : ''}₹${r.ceExit.pnl}` : '—'}</td>
                        <td>{legStatusBadge(r.ceLeg, r.ceExit)}</td>
                      </>) : (<>
                        <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.6875rem' }}>—</td>
                      </>)}
                      {/* PE leg */}
                      {r.peLeg ? (<>
                        <td style={mono}>{r.peLeg.strike}</td>
                        <td style={mono}>₹{r.peLeg.entryPrice}</td>
                        <td style={mono}>{r.peExit ? `₹${r.peExit.exitPrice}` : '—'}</td>
                        <td style={pnlStyle(r.peExit?.pnl)}>{r.peExit?.pnl !== undefined ? `${r.peExit.pnl >= 0 ? '+' : ''}₹${r.peExit.pnl}` : '—'}</td>
                        <td>{legStatusBadge(r.peLeg, r.peExit)}</td>
                      </>) : (<>
                        <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.6875rem' }}>—</td>
                      </>)}
                      <td style={pnlStyle(r.totalPnl)}>
                        <strong>{r.totalPnl !== 0 ? `${r.totalPnl >= 0 ? '+' : ''}₹${r.totalPnl.toLocaleString()}` : '—'}</strong>
                      </td>
                    </tr>
                    {/* Extra adjustment legs */}
                    {r.extraLegs.map((adj, idx) => {
                      const adjExit = r.extraExits[idx] || null
                      return (
                        <tr key={adj.id} style={{ background: 'rgba(167,139,250,0.04)' }}>
                          <td></td>
                          <td style={{ fontSize: '0.625rem', color: 'var(--purple)' }}>
                            {new Date(adj.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td><span className="badge badge-exit" style={{ fontSize: '0.5625rem', background: 'rgba(167,139,250,0.12)', color: 'var(--purple)', borderColor: 'rgba(167,139,250,0.3)' }}>adjust</span></td>
                          {adj.optionType === 'CE' ? (<>
                            <td style={mono}>{adj.strike}</td>
                            <td style={mono}>₹{adj.entryPrice}</td>
                            <td style={mono}>{adjExit ? `₹${adjExit.exitPrice}` : '—'}</td>
                            <td style={pnlStyle(adjExit?.pnl)}>{adjExit?.pnl !== undefined ? `${adjExit.pnl >= 0 ? '+' : ''}₹${adjExit.pnl}` : '—'}</td>
                            <td>{legStatusBadge(adj, adjExit)}</td>
                            <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>—</td>
                          </>) : (<>
                            <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>—</td>
                            <td style={mono}>{adj.strike}</td>
                            <td style={mono}>₹{adj.entryPrice}</td>
                            <td style={mono}>{adjExit ? `₹${adjExit.exitPrice}` : '—'}</td>
                            <td style={pnlStyle(adjExit?.pnl)}>{adjExit?.pnl !== undefined ? `${adjExit.pnl >= 0 ? '+' : ''}₹${adjExit.pnl}` : '—'}</td>
                            <td>{legStatusBadge(adj, adjExit)}</td>
                          </>)}
                          <td style={pnlStyle(adjExit?.pnl)}>
                            {adjExit?.pnl !== undefined ? `${adjExit.pnl >= 0 ? '+' : ''}₹${adjExit.pnl}` : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────
export function AdminReports() {
  const { executions, trades, panicExit } = useExecutions()
  const { users: _users } = useAuth()
  const { addToast } = useToast()
  const traderNames = useTraderNames()
  const getTraderName = (id: string) => traderNames[id] || id
  const [tab, setTab] = useState<Tab>('strategy')
  const [stratFilter, setStratFilter] = useState<string>('all')
  const [traderFilter, setTraderFilter] = useState<string>('all')
  const [panicModal, setPanicModal] = useState<{ level: 'all' | 'strategy' | 'trader'; target: string } | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0] })
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0])

  const handlePanicConfirm = () => {
    if (!panicModal) return
    if (panicModal.level === 'all') {
      panicExit('all')
      addToast('Panic exit — ALL positions squared off', 'warning', 6000)
    } else if (panicModal.level === 'strategy') {
      const stratExecs = executions.filter(e => e.strategyType === panicModal.target && e.status === 'running')
      stratExecs.forEach(e => panicExit('set', e.id))
      addToast(`Panic exit — ${strategyMeta[panicModal.target]?.label || panicModal.target} positions squared off`, 'warning', 6000)
    } else {
      const traderExecs = executions.filter(e => e.traderId === panicModal.target && e.status === 'running')
      traderExecs.forEach(e => panicExit('set', e.id))
      addToast(`Panic exit — ${getTraderName(panicModal.target)} positions squared off`, 'warning', 6000)
    }
    setPanicModal(null)
  }

  const hasRunning = executions.some(e => e.status === 'running')

  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      const exec = executions.find((e) => e.id === t.executionId)
      if (!exec) return false
      if (stratFilter !== 'all' && exec.strategyType !== stratFilter) return false
      if (traderFilter !== 'all' && exec.traderId !== traderFilter) return false
      const tradeDate = t.timestamp.split('T')[0]
      if (fromDate && tradeDate < fromDate) return false
      if (toDate && tradeDate > toDate) return false
      if (selectedDate && tradeDate !== selectedDate) return false
      return true
    })
  }, [trades, executions, stratFilter, traderFilter, selectedDate, fromDate, toDate])

  const strategySummary = useMemo(() => {
    const types = ['rsi_straddle', 'trending_expiry'] as const
    return types.map((type) => {
      const execs = executions.filter((e) => e.strategyType === type)
      const typeTrades = trades.filter((t) => execs.some((e) => e.id === t.executionId))
      const totalPnl = execs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0)
      const realized = execs.reduce((s, e) => s + (e.state.mtm?.realizedPnl || 0), 0)
      const unrealized = execs.reduce((s, e) => s + (e.state.mtm?.unrealizedPnl || 0), 0)
      const running = execs.filter((e) => e.status === 'running').length
      const totalSets = execs.length
      const entries = typeTrades.filter((t) => t.tradeType === 'entry' || t.tradeType === 'reentry').length
      const exits = typeTrades.filter((t) => t.tradeType.startsWith('exit')).length
      const slHits = typeTrades.filter((t) => t.tradeType === 'exit_sl').length
      const targetHits = typeTrades.filter((t) => t.tradeType === 'exit_target').length
      const panics = typeTrades.filter((t) => t.tradeType === 'panic_exit').length
      const traders = Array.from(new Set(execs.map((e) => e.traderId)))
      return { type, meta: strategyMeta[type], execs, trades: typeTrades, totalPnl, realized, unrealized, running, totalSets, entries, exits, slHits, targetHits, panics, traders }
    })
  }, [executions, trades])

  const traderSummary = useMemo(() => {
    const traderIds = Array.from(new Set(executions.map((e) => e.traderId)))
    return traderIds.map((tid) => {
      const execs = executions.filter((e) => e.traderId === tid)
      const tTrades = trades.filter((t) => execs.some((e) => e.id === t.executionId))
      const totalPnl = execs.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0)
      const running = execs.filter((e) => e.status === 'running').length
      const rsiSets = execs.filter((e) => e.strategyType === 'rsi_straddle').length
      const trendSets = execs.filter((e) => e.strategyType === 'trending_expiry').length
      const entries = tTrades.filter((t) => t.tradeType === 'entry' || t.tradeType === 'reentry').length
      const exits = tTrades.filter((t) => t.tradeType.startsWith('exit')).length
      return { id: tid, name: getTraderName(tid), execs, trades: tTrades, totalPnl, running, rsiSets, trendSets, entries, exits }
    })
  }, [executions, trades])

  // ─── Strategy Tab with multi-leg view ───
  const renderStratTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {strategySummary.map((s) => {
        const pnlColor = s.totalPnl >= 0 ? 'var(--green)' : 'var(--red)'
        const winRate = s.exits > 0 ? ((s.targetHits / s.exits) * 100).toFixed(0) : '—'
        return (
          <div key={s.type} className="card">
              <div className="card-header">
                <span className="card-title" style={{ color: s.meta.color }}>{s.meta.icon} {s.meta.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{s.totalSets} sets · {s.running} running · {s.traders.length} traders</span>
                  {s.running > 0 && (
                    <button className="btn btn-sm btn-red" onClick={() => setPanicModal({ level: 'strategy', target: s.type })}>
                      ⚠ Panic {s.meta.label.split(' ')[0]}
                    </button>
                  )}
                </div>
              </div>

            <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="stats-card" style={{ '--card-accent': pnlColor } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Total P&L <InfoTip term="pnl" /></span></div>
                <div className="stats-value-wrap"><span className="stats-value" style={{ color: pnlColor, WebkitTextFillColor: pnlColor }}>{s.totalPnl >= 0 ? '+' : ''}₹{s.totalPnl.toLocaleString()}</span></div>
              </div>
              <div className="stats-card" style={{ '--card-accent': 'var(--accent)' } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Realized <InfoTip term="realizedPnl" /></span></div>
                <div className="stats-value-wrap"><span className="stats-value">{s.realized >= 0 ? '+' : ''}₹{s.realized.toLocaleString()}</span></div>
              </div>
              <div className="stats-card" style={{ '--card-accent': 'var(--purple)' } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Win Rate <InfoTip term="winRate" /></span></div>
                <div className="stats-value-wrap"><span className="stats-value">{winRate}{winRate !== '—' ? '%' : ''}</span></div>
                <div className="stats-subtitle">{s.targetHits} targets · {s.slHits} SL hits</div>
              </div>
              <div className="stats-card" style={{ '--card-accent': s.panics > 0 ? 'var(--red)' : 'var(--green)' } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Panics</span></div>
                <div className="stats-value-wrap"><span className="stats-value">{s.panics}</span></div>
                <div className="stats-subtitle">{s.entries} entries · {s.exits} exits</div>
              </div>
            </div>

            <h4 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.75rem', letterSpacing: '0.04em' }}>
              Position Legs by Set
            </h4>

            {s.execs.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No execution sets.</p>
            ) : (
              s.execs.map((exec) => {
                const setTrades = s.trades.filter(t => t.executionId === exec.id)
                const p = exec.params as any
                return (
                  <SetLegTable
                    key={exec.id}
                    setTrades={setTrades}
                    setName={`${exec.setName} — ${getTraderName(exec.traderId)}`}
                    stratColor={s.meta.color}
                    execInfo={{
                      underlying: exec.underlying,
                      expiry: p?.expiryType || 'weekly',
                      strike: p?.strikeSelectionMode || 'ATM',
                      lots: p?.quantityLots,
                      posType: p?.positionType || 'straddle',
                    }}
                  />
                )
              })
            )}
          </div>
        )
      })}
    </div>
  )

  // ─── Trader Tab with multi-leg view ───
  const renderTraderTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {traderSummary.map((tr) => {
        const pnlColor = tr.totalPnl >= 0 ? 'var(--green)' : 'var(--red)'
        return (
          <div key={tr.id} className="card">
              <div className="card-header">
                <span className="card-title" style={{ color: pnlColor }}>⚇ {tr.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {tr.rsiSets} RSI · {tr.trendSets} Trend · {tr.running} running
                  </span>
                  {tr.running > 0 && (
                    <button className="btn btn-sm btn-red" onClick={() => setPanicModal({ level: 'trader', target: tr.id })}>
                      ⚠ Panic
                    </button>
                  )}
                </div>
              </div>

            <div className="stats-grid" style={{ marginBottom: '1.25rem' }}>
              <div className="stats-card" style={{ '--card-accent': pnlColor } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Total P&L <InfoTip term="pnl" /></span></div>
                <div className="stats-value-wrap"><span className="stats-value" style={{ color: pnlColor, WebkitTextFillColor: pnlColor }}>{tr.totalPnl >= 0 ? '+' : ''}₹{tr.totalPnl.toLocaleString()}</span></div>
              </div>
              <div className="stats-card" style={{ '--card-accent': 'var(--accent)' } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Total Sets</span></div>
                <div className="stats-value-wrap"><span className="stats-value">{tr.execs.length}</span></div>
              </div>
              <div className="stats-card" style={{ '--card-accent': 'var(--purple)' } as React.CSSProperties}>
                <div className="stats-card-top"><span className="stats-title">Entries</span></div>
                <div className="stats-value-wrap"><span className="stats-value">{tr.entries}</span></div>
                <div className="stats-subtitle">{tr.exits} exits</div>
              </div>
            </div>

            <h4 style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.75rem', letterSpacing: '0.04em' }}>
              Position Legs by Set
            </h4>

            {tr.execs.map((exec) => {
              const setTrades = tr.trades.filter(t => t.executionId === exec.id)
              const meta = strategyMeta[exec.strategyType]
              const p = exec.params as any
              return (
                <SetLegTable
                  key={exec.id}
                  setTrades={setTrades}
                  setName={`${exec.setName} — ${meta?.icon} ${meta?.label}`}
                  stratColor={meta?.color || 'var(--accent)'}
                  execInfo={{
                    underlying: exec.underlying,
                    expiry: p?.expiryType || 'weekly',
                    strike: p?.strikeSelectionMode || 'ATM',
                    lots: p?.quantityLots,
                    posType: p?.positionType || 'straddle',
                  }}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )

  // ─── Transactions Tab (flat log, kept for audit) ───
  // ─── Scrip Tab ───
  const scripSummary = useMemo(() => {
    const byUnderlying: Record<string, { underlying: string; sets: number; realized: number; unrealized: number; totalPnl: number; entries: number; exits: number }> = {}
    executions.forEach(e => {
      const u = e.underlying
      if (!byUnderlying[u]) byUnderlying[u] = { underlying: u, sets: 0, realized: 0, unrealized: 0, totalPnl: 0, entries: 0, exits: 0 }
      byUnderlying[u].sets += 1
      byUnderlying[u].realized += e.state.mtm?.realizedPnl || 0
      byUnderlying[u].unrealized += e.state.mtm?.unrealizedPnl || 0
      byUnderlying[u].totalPnl += e.state.mtm?.totalPnl || 0
      const execTrades = trades.filter(t => t.executionId === e.id)
      byUnderlying[u].entries += execTrades.filter(t => t.tradeType === 'entry' || t.tradeType === 'reentry').length
      byUnderlying[u].exits += execTrades.filter(t => t.tradeType.startsWith('exit') || t.tradeType === 'panic_exit').length
    })
    return Object.values(byUnderlying)
  }, [executions, trades])

  const renderScripTab = () => (
    <div className="card">
      <div className="card-header">
        <span className="card-title">📊 Scrip-wise Performance</span>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Underlying</th><th>Sets</th><th>Realized</th><th>Unrealized</th><th>Total P&L</th><th>Entries</th><th>Exits</th></tr>
          </thead>
          <tbody>
            {scripSummary.map(s => (
              <tr key={s.underlying}>
                <td><strong>{s.underlying}</strong></td>
                <td>{s.sets}</td>
                <td style={pnlStyle(s.realized)}>{s.realized >= 0 ? '+' : ''}{`\u20B9`}{Math.abs(s.realized).toLocaleString()}</td>
                <td style={{ ...mono, color: 'var(--text-secondary)' }}>{s.unrealized >= 0 ? '+' : ''}{`\u20B9`}{Math.abs(s.unrealized).toLocaleString()}</td>
                <td style={{ ...pnlStyle(s.totalPnl), fontWeight: 700 }}>{s.totalPnl >= 0 ? '+' : ''}{`\u20B9`}{Math.abs(s.totalPnl).toLocaleString()}</td>
                <td>{s.entries}</td>
                <td>{s.exits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  // ─── P&L Tab ───
  const [pnlPeriod, setPnlPeriod] = useState(90)
  const [pnlDimension, setPnlDimension] = useState<PnlDimension>('overall')
  // Build P&L from real trade data
  const pnlData = useMemo(() => buildRealPnl(trades, executions, pnlPeriod), [trades, executions, pnlPeriod])
  const hasRealData = pnlData.length > 0
  const pnlTotals = useMemo(() => {
    const total = pnlData.reduce((s, r) => s + r.total, 0)
    const realized = pnlData.reduce((s, r) => s + r.realized, 0)
    const winDays = pnlData.filter(r => r.total >= 0).length
    const lossDays = pnlData.filter(r => r.total < 0).length
    const best = Math.max(...pnlData.map(r => r.total), 0)
    const worst = Math.min(...pnlData.map(r => r.total), 0)
    const avg = pnlData.length > 0 ? Math.round(total / pnlData.length) : 0
    return { total, realized, winDays, lossDays, best, worst, avg }
  }, [pnlData])

  const renderPnlTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="filter-group">
          <label>Dimension</label>
          <select className="cfg-input" value={pnlDimension} onChange={(e) => setPnlDimension(e.target.value as PnlDimension)}>
            <option value="overall">Overall</option>
            <option value="strategy">By Strategy</option>
            <option value="scrip">By Scrip</option>
            <option value="trader">By Trader</option>
            <option value="set">By Set</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', marginLeft: 'auto' }}>
          {[7, 30, 90].map(d => (
            <button key={d} className={`btn btn-sm ${pnlPeriod === d ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPnlPeriod(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-strip">
        <div className="kpi-card">
          <div className="kpi-label">Total P&L <InfoTip term="pnl" /></div>
          <div className={`kpi-value ${pnlTotals.total >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {pnlTotals.total >= 0 ? '+' : ''}₹{Math.abs(pnlTotals.total).toLocaleString()}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Realized <InfoTip term="realizedPnl" /></div>
          <div className={`kpi-value ${pnlTotals.realized >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {pnlTotals.realized >= 0 ? '+' : ''}₹{Math.abs(pnlTotals.realized).toLocaleString()}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Win Days</div>
          <div className="kpi-value kpi-positive">{pnlTotals.winDays}</div>
          <div className="kpi-sub">{pnlTotals.lossDays} loss days</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Best Day <InfoTip term="maxProfit" /></div>
          <div className="kpi-value kpi-positive">+₹{pnlTotals.best.toLocaleString()}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Worst Day <InfoTip term="maxLoss" /></div>
          <div className="kpi-value kpi-negative">₹{pnlTotals.worst.toLocaleString()}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Avg Day <InfoTip term="avgDailyPnl" /></div>
          <div className={`kpi-value ${pnlTotals.avg >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>
            {pnlTotals.avg >= 0 ? '+' : ''}₹{Math.abs(pnlTotals.avg).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Daily P&L Bar Chart */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📊 Daily P&L</span>
          {!hasRealData && <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No trades yet — data appears after first live session</span>}
        </div>
        {hasRealData ? (
          <div style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px' }}>
            {pnlData.map((row) => {
              const maxAbs = Math.max(...pnlData.map(r => Math.abs(r.total)), 1)
              const h = Math.max(Math.abs(row.total) / maxAbs * 100, 2)
              return (
                <div key={row.date} style={{
                  flex: 1, minWidth: '3px',
                  height: `${h}%`,
                  background: row.total >= 0 ? 'var(--green)' : 'var(--red)',
                  borderRadius: '1px 1px 0 0',
                  opacity: 0.8,
                }} title={`${row.dateLabel}: ₹${row.total.toLocaleString()}`} />
              )
            })}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
            📭 No historical trade data yet. Start your first strategy session to populate this chart.
          </div>
        )}
      </div>

      {/* Cumulative P&L Chart (Recharts) */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📈 Cumulative P&L <InfoTip term="cumulativePnl" /></span>
          {(() => {
            let cum = 0
            const pts = pnlData.map(r => { cum += r.total; return cum })
            const finalVal = pts[pts.length - 1] || 0
            return (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: '0.9375rem', color: finalVal >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {finalVal >= 0 ? '+' : ''}₹{Math.abs(finalVal).toLocaleString()}
              </span>
            )
          })()}
        </div>
        <div style={{ padding: '0.5rem 0.5rem 0', height: 220 }}>
          {hasRealData ? (() => {
            let cum = 0
            const chartData = pnlData.map(r => {
              cum += r.total
              return { date: r.dateLabel, cumulative: cum, daily: r.total }
            })
            const finalVal = chartData[chartData.length - 1]?.cumulative || 0
            const isPositive = finalVal >= 0
            const strokeColor = isPositive ? '#22c55e' : '#ef4444'
            const fillColor = isPositive ? 'url(#cumGreenGrad)' : 'url(#cumRedGrad)'
            return (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cumGreenGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="cumRedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(chartData.length / 8))} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}${(v/1000).toFixed(0)}K`} width={50} />
                  <ReferenceLine y={0} stroke="var(--text-muted)" strokeWidth={0.8} opacity={0.6} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '0.75rem', fontFamily: "'JetBrains Mono', monospace" }}
                    labelStyle={{ color: 'var(--text-primary)', fontWeight: 700 }}
                    formatter={(value: number) => [`${value >= 0 ? '+' : ''}₹${Math.abs(value).toLocaleString()}`, 'Cumulative']}
                  />
                  <Area type="monotone" dataKey="cumulative" stroke={strokeColor} strokeWidth={2.5} fill={fillColor} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--bg-card)' }} />
                </AreaChart>
              </ResponsiveContainer>
            )
          })() : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              📭 Chart populates automatically after your first live trades
            </div>
          )}
        </div>
      </div>

      {/* Day-by-Day Table */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">📅 Day-by-Day Breakdown <InfoTip term="dayByDay" /></span>
          <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{pnlData.length} trading days · last {pnlPeriod} calendar days</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ color: '#38bdf8' }}>RSI Straddle</th>
                <th style={{ color: '#a78bfa' }}>Trending Expiry</th>
                <th>Realized</th><th>Unrealized</th><th>Day P&L</th>
                <th>Cumulative</th><th>Entries</th><th>Exits</th>
                <th>SL Hits</th><th>Targets</th><th>Result</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let cum = 0
                const rows = [...pnlData]
                const cumArr = rows.map(r => { cum += r.total; return cum })
                return rows.map((row, i) => {
                  const c = cumArr[i]
                  return (
                    <tr key={row.date} onClick={() => { setSelectedDate(row.date); setTab('transactions') }}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-glow)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{row.dateLabel}</td>
                      <td style={{ ...mono, fontWeight: 600, color: row.rsiPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {row.rsiPnl >= 0 ? '+' : ''}₹{Math.abs(row.rsiPnl).toLocaleString()}
                      </td>
                      <td style={{ ...mono, fontWeight: 600, color: row.trendPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {row.trendPnl >= 0 ? '+' : ''}₹{Math.abs(row.trendPnl).toLocaleString()}
                      </td>
                      <td style={{ ...mono, color: row.realized >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {row.realized >= 0 ? '+' : ''}₹{Math.abs(row.realized).toLocaleString()}
                      </td>
                      <td style={{ ...mono, color: 'var(--text-secondary)' }}>
                        {row.unrealized >= 0 ? '+' : ''}₹{Math.abs(row.unrealized).toLocaleString()}
                      </td>
                      <td style={{ ...mono, fontWeight: 700, color: row.total >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {row.total >= 0 ? '+' : ''}₹{Math.abs(row.total).toLocaleString()}
                      </td>
                      <td style={{ ...mono, fontWeight: 700, color: c >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {c >= 0 ? '+' : ''}₹{Math.abs(c).toLocaleString()}
                      </td>
                      <td style={{ ...mono, fontWeight: 600 }}>{row.entries}</td>
                      <td style={{ ...mono, fontWeight: 600 }}>{row.exits}</td>
                      <td style={{ ...mono, fontWeight: 700, color: row.slHits > 0 ? 'var(--red)' : 'var(--text-muted)' }}>{row.slHits}</td>
                      <td style={{ ...mono, fontWeight: 700, color: row.targets > 0 ? 'var(--green)' : 'var(--text-muted)' }}>{row.targets}</td>
                      <td>
                        <span className={`badge ${row.total >= 0 ? 'badge-target' : 'badge-sl'}`}>
                          {row.total >= 0 ? 'WIN' : 'LOSS'}
                        </span>
                      </td>
                    </tr>
                  )
                })
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // ─── Transactions Tab (flat log, kept for audit) ───
  const renderTransactionsTab = () => (
    <div>
      <div className="filter-bar">
        <div className="filter-group">
          <label>Strategy</label>
          <select className="cfg-input" value={stratFilter} onChange={(e) => setStratFilter(e.target.value)}>
            <option value="all">All Strategies</option>
            <option value="rsi_straddle">RSI Straddle</option>
            <option value="trending_expiry">Trending Expiry</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Trader</label>
          <select className="cfg-input" value={traderFilter} onChange={(e) => setTraderFilter(e.target.value)}>
            <option value="all">All Traders</option>
            {Array.from(new Set(executions.map((e) => e.traderId))).map((tid) => (
              <option key={tid} value={tid}>{getTraderName(tid)}</option>
            ))}
          </select>
        </div>
        {selectedDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span className="badge badge-entry" style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}>
              📅 Filtered: {new Date(selectedDate + 'T00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button className="btn btn-sm btn-ghost" style={{ fontSize: '0.6875rem' }}
              onClick={() => setSelectedDate(null)}>✕ Clear</button>
          </div>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {filteredTrades.length} transaction{filteredTrades.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="card">
        {filteredTrades.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            No transactions match the selected filters.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Time</th><th>Trader</th><th>Set</th><th>Strategy</th><th>Underlying</th><th>Expiry</th><th>Strike</th><th>Type</th><th>Option</th><th>Entry</th><th>Exit</th><th>Qty</th><th>P&L</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {filteredTrades.slice().reverse().map((t) => {
                  const exec = executions.find((e) => e.id === t.executionId)
                  const meta = exec ? strategyMeta[exec.strategyType] : null
                  const ep = exec?.params as any
                  return (
                    <tr key={t.id}>
                      <td style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(t.timestamp).toLocaleString()}</td>
                      <td>{exec ? getTraderName(exec.traderId) : '—'}</td>
                      <td><strong>{exec?.setName || '—'}</strong></td>
                      <td>{meta ? <span style={{ color: meta.color }}>{meta.icon} {meta.label}</span> : '—'}</td>
                      <td>{exec?.underlying || '—'}</td>
                      <td><span className="badge badge-entry" style={{ fontSize: '0.6875rem' }}>{ep?.expiryType || '—'}</span></td>
                      <td><code style={{ fontSize: '0.6875rem' }}>{ep?.strikeSelectionMode || '—'}</code></td>
                      <td><span className={`badge ${t.tradeType.startsWith('exit') ? 'badge-sl' : t.tradeType === 'entry' ? 'badge-target' : t.tradeType === 'reentry' ? 'badge-entry' : 'badge-exit'}`}>{t.tradeType.replace(/_/g, ' ')}</span></td>
                      <td><span style={{ color: t.optionType === 'CE' ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>{t.optionType}</span></td>
                      <td style={mono}>₹{t.entryPrice}</td>
                      <td style={mono}>{t.exitPrice !== undefined ? `₹${t.exitPrice}` : '—'}</td>
                      <td>{t.filledQuantity || t.quantity}</td>
                      <td style={pnlStyle(t.pnl)}>
                        {t.pnl !== undefined ? `${t.pnl >= 0 ? '+' : ''}₹${t.pnl}` : '—'}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.reason || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div>
      <div className="page-head">
        <h1>Reports</h1>
        <p className="page-sub">Strategy-wise & trader-wise performance with multi-leg position tracking</p>
      </div>

      {/* Panic strip */}
      {hasRunning && (
        <div className="dash-control-strip" style={{ marginBottom: '1.25rem' }}>
          <div className="dash-mtm-strip">
            <div className="mtm-item">
              <span className="mtm-label">Running</span>
              <span className="mtm-val">{executions.filter(e => e.status === 'running').length}</span>
            </div>
            <div className="mtm-item mtm-item-divider" />
            <div className="mtm-item">
              <span className="mtm-label">Total MTM <InfoTip term="mtm" /></span>
              <span className={`mtm-val ${executions.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0) >= 0 ? 'green' : 'red'}`}>
                {executions.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0) >= 0 ? '+' : ''}₹{executions.reduce((s, e) => s + (e.state.mtm?.totalPnl || 0), 0).toLocaleString()}
              </span>
            </div>
            <div className="mtm-item mtm-item-divider" />
            <div className="mtm-item">
              <span className="mtm-label">Traders</span>
              <span className="mtm-val">{Array.from(new Set(executions.map(e => e.traderId))).length}</span>
            </div>
          </div>
          <div className="dash-panic-zone">
            <button className="btn btn-red btn-sm" onClick={() => setPanicModal({ level: 'all', target: 'ALL' })}>
              ⚠ Panic All
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div className="type-tabs" style={{ marginBottom: 0 }}>
          <button className={`type-tab ${tab === 'strategy' ? 'active' : ''}`} onClick={() => setTab('strategy')}>
            ⟐ Strategy-wise
          </button>
          <button className={`type-tab ${tab === 'trader' ? 'active' : ''}`} onClick={() => setTab('trader')}>
            ⚇ Trader-wise
          </button>
          <button className={`type-tab ${tab === 'scrip' ? 'active' : ''}`} onClick={() => setTab('scrip')}>
            📊 Scrip-wise
          </button>
          <button className={`type-tab ${tab === 'pnl' ? 'active' : ''}`} onClick={() => setTab('pnl')}>
            📈 P&L Report
          </button>
          <button className={`type-tab ${tab === 'transactions' ? 'active' : ''}`} onClick={() => setTab('transactions')}>
            ◇ All Transactions
            <span className="type-tab-count">{trades.length}</span>
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>From</label>
            <input type="date" className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>To</label>
            <input type="date" className="cfg-input" style={{ width: 'auto', padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 7); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>7d</button>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>30d</button>
            <button className="btn btn-sm btn-ghost" style={{ padding: '0.2rem 0.5rem', fontSize: '0.6875rem' }} onClick={() => { const d = new Date(); d.setDate(d.getDate() - 90); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]) }}>90d</button>
          </div>
          <button className="btn btn-sm btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            onClick={() => {
              if (tab === 'transactions') {
                exportToExcel(trades.map(t => ({
                  Time: new Date(t.timestamp).toLocaleString(), Set: t.executionId, Strategy: t.strategyType,
                  Underlying: t.underlying, Expiry: t.expiry, Strike: t.strike, OptionType: t.optionType,
                  TradeType: t.tradeType, EntryPrice: t.entryPrice, ExitPrice: t.exitPrice ?? '',
                  Qty: t.qty, PnL: t.pnl ?? '', Reason: t.reason || '',
                })), `Aalap_Transactions_${new Date().toISOString().split('T')[0]}`)
              } else if (tab === 'pnl') {
                exportToExcel(pnlData.map(r => ({
                  Date: r.dateLabel, Realized: r.realized, Unrealized: r.unrealized,
                  'Day P&L': r.total, Entries: r.entries, Exits: r.exits,
                  'SL Hits': r.slHits, Targets: r.targets, Result: r.total >= 0 ? 'WIN' : 'LOSS',
                })), `Aalap_PnL_${pnlPeriod}d_${new Date().toISOString().split('T')[0]}`)
              } else if (tab === 'scrip') {
                exportToExcel(scripSummary.map(s => ({
                  Underlying: s.underlying, Sets: s.sets, Realized: s.realized,
                  Unrealized: s.unrealized, 'Total P&L': s.totalPnl, Entries: s.entries, Exits: s.exits,
                })), `Aalap_Scrip_${new Date().toISOString().split('T')[0]}`)
              } else {
                exportToExcel(executions.map(e => ({
                  Set: e.setName, Strategy: e.strategyType, Trader: e.traderId, Underlying: e.underlying,
                  Status: e.status, Premium: e.state.currentPremium, PnL: e.state.pnl,
                  Realized: e.state.mtm?.realizedPnl ?? 0, Unrealized: e.state.mtm?.unrealizedPnl ?? 0,
                  TotalMTM: e.state.mtm?.totalPnl ?? 0, ReEntries: e.state.reentryCount,
                })), `Aalap_${tab}_${new Date().toISOString().split('T')[0]}`)
              }
              addToast('Excel exported successfully', 'success')
            }}>
            ⬇ Export
          </button>
        </div>
      </div>


      {tab === 'strategy' && renderStratTab()}
      {tab === 'trader' && renderTraderTab()}
      {tab === 'scrip' && renderScripTab()}
      {tab === 'pnl' && renderPnlTab()}
      {tab === 'transactions' && renderTransactionsTab()}

      {/* Panic Confirmation Modal */}
      {panicModal && (
        <div className="modal-overlay" onClick={() => setPanicModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--red)' }}>⚠ Panic Exit</h3>
              <button className="modal-close" onClick={() => setPanicModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
                Square off <strong>{
                  panicModal.level === 'all' ? 'ALL POSITIONS'
                  : panicModal.level === 'strategy' ? `${strategyMeta[panicModal.target]?.label || panicModal.target} positions`
                  : `${getTraderName(panicModal.target)}'s positions`
                }</strong> immediately?
              </p>
              <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                This action will immediately square off all open positions. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPanicModal(null)}>Cancel</button>
              <button className="btn btn-red" onClick={handlePanicConfirm}>⚠ Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

